import BaseToolEngine from "@shared/GenericTools/engines/BaseToolEngine";
import type {CanvasTimelineEngineInterface, EnginePayload, ToolConfig} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {CanvasTimelineEngineInterfaceToken} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {ToolEngineValidationResult} from "@shared/GenericTools/EngineInterfaces/ToolEngineDataTypes";
import type {ToolWorkContext} from "./ToolWorkLifecycleTypes";
import {normalizeTimelineCanvasConfig, normalizeTimelineCanvasPayload, getTimelineTime} from "@shared/GenericTools/timelineCanvas/TimelineCanvasModel";

export default class CanvasTimelineToolEngine extends BaseToolEngine implements CanvasTimelineEngineInterface {
	getEngineInterfaceTokens() {return [CanvasTimelineEngineInterfaceToken]}

	normalizeConfig(config: ToolConfig = {}): ToolConfig {
		return {...config, timelineCanvas: normalizeTimelineCanvasConfig(config.timelineCanvas || config)}
	}

	createDefaultPayload(config: ToolConfig = {}): EnginePayload {
		return {canvasTimeline: {events: [], styleClassByEventId: {}, layoutByEventId: {}, groups: [], links: [], config: this.normalizeConfig(config).timelineCanvas}}
	}

	normalizeWorkPayload(payload: EnginePayload, config: ToolConfig, context: ToolWorkContext, isSubmission: boolean): EnginePayload {
		if (isSubmission) this.assertWorkValidation(this.validateWorkPayload(payload, config))
		return this.normalizePayload(payload, config)
	}

	normalizePayload(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		const timeline = normalizeTimelineCanvasPayload(payload)
		return {...payload, canvasTimeline: {...timeline, config: this.normalizeConfig(config).timelineCanvas}}
	}

	validateConfig(config: ToolConfig = {}): ToolEngineValidationResult {
		const result = new ToolEngineValidationResult()
		const normalized = this.normalizeConfig(config).timelineCanvas as ToolConfig
		for (const key of ["lanes", "types"]) {
			const ids = (normalized[key] as ToolConfig[]).map(item => String(item.id || ""))
			if (ids.some(id => !id) || new Set(ids).size !== ids.length) result.errors.push(`Invalid timeline ${key}`)
		}
		const laneIds = new Set((normalized.lanes as ToolConfig[]).map(item => item.id))
		if ((normalized.types as ToolConfig[]).some(item => !laneIds.has(item.laneId))) result.errors.push("Timeline type references an unknown lane")
		result.isValid = result.errors.length === 0
		return result
	}

	validateWorkPayload(payload: EnginePayload = {}, config: ToolConfig = {}): ToolEngineValidationResult {
		const result = this.validateConfig(config)
		const events = normalizeTimelineCanvasPayload(payload).events as ToolConfig[]
		const groups = normalizeTimelineCanvasPayload(payload).groups as ToolConfig[]
		const ids = events.map(event => event.id)
		const normalizedConfig = this.normalizeConfig(config).timelineCanvas as ToolConfig
		if (ids.some(id => !id) || ids.length !== new Set(ids).size) result.errors.push("Invalid timeline event IDs")
		if (events.some(event => {
			const timing = event.timing as ToolConfig | undefined
			return ["point", "range"].includes(String(timing?.kind)) && timing?.start && (timing.kind !== "range" || timing.end) && !getTimelineTime(event, normalizedConfig)
		})) result.errors.push("Invalid timeline time")
		const groupIds = groups.map(group => String(group.id || ""))
		if (groupIds.some(id => !id) || new Set(groupIds).size !== groupIds.length || groups.some(group => (group.eventIds as string[]).length < 2 || (group.eventIds as string[]).some(id => !ids.includes(id)))) result.errors.push("Invalid timeline groups")
		result.isValid = result.errors.length === 0
		return result
	}

	createSummaryInput(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		return this.normalizePayload(payload, config)
	}

	addEvent(payload: EnginePayload = {}, event: ToolConfig = {}, config: ToolConfig = {}): EnginePayload {
		const normalized = this.normalizePayload(payload, config)
		const timeline = normalized.canvasTimeline as ToolConfig
		const events = timeline.events as ToolConfig[]
		const next = {...event, id: String(event.id || `event_${Date.now()}_${Math.random().toString(36).slice(2)}`), order: events.length, timing: event.timing || {kind: "unknown"}}
		return {...normalized, canvasTimeline: {...timeline, events: [...events, next]}}
	}

	updateEvent(payload: EnginePayload = {}, eventId = "", changes: ToolConfig = {}, config: ToolConfig = {}): EnginePayload {
		const normalized = this.normalizePayload(payload, config)
		const timeline = normalized.canvasTimeline as ToolConfig
		return {...normalized, canvasTimeline: {...timeline, events: (timeline.events as ToolConfig[]).map(event => event.id === eventId ? {...event, ...changes, id: eventId} : event)}}
	}

	deleteEvent(payload: EnginePayload = {}, eventId = "", config: ToolConfig = {}): EnginePayload {
		const normalized = this.normalizePayload(payload, config)
		const timeline = normalized.canvasTimeline as ToolConfig
		const styles = {...timeline.styleClassByEventId as ToolConfig}
		const layout = {...timeline.layoutByEventId as ToolConfig}
		delete styles[eventId]
		delete layout[eventId]
		const groups = (timeline.groups as ToolConfig[]).map(group => ({...group, eventIds: (group.eventIds as string[]).filter(id => id !== eventId)})).filter(group => (group.eventIds as string[]).length >= 2)
		const links = (timeline.links as ToolConfig[]).filter(link => link.sourceEventId !== eventId && link.targetEventId !== eventId)
		return {...normalized, canvasTimeline: {...timeline, events: (timeline.events as ToolConfig[]).filter(event => event.id !== eventId), styleClassByEventId: styles, layoutByEventId: layout, groups, links}}
	}

	moveEvent(payload: EnginePayload = {}, eventId = "", target: ToolConfig = {}, config: ToolConfig = {}): EnginePayload {
		const normalized = this.normalizePayload(payload, config)
		const timeline = normalized.canvasTimeline as ToolConfig
		const events = [...timeline.events as ToolConfig[]]
		const placement = target.layout as ToolConfig | undefined
		const layout = placement ? {...timeline.layoutByEventId as ToolConfig, [eventId]: placement} : timeline.layoutByEventId
		const moved = {...normalized, canvasTimeline: {...timeline, layoutByEventId: layout}}
		if (target.timing) return this.updateEvent(moved, eventId, {timing: target.timing}, config)
		const index = events.findIndex(event => event.id === eventId)
		if (index >= 0 && Number.isFinite(Number(target.order))) {
			const [event] = events.splice(index, 1)
			events.splice(Math.max(0, Math.min(events.length, Number(target.order))), 0, event)
			return {...moved, canvasTimeline: {...moved.canvasTimeline as ToolConfig, events: events.map((item, order) => ({...item, order}))}}
		}
		return {...moved, canvasTimeline: {...moved.canvasTimeline as ToolConfig, events}}
	}

	assignStyleClass(payload: EnginePayload = {}, eventId = "", classId = "", config: ToolConfig = {}): EnginePayload {
		const normalized = this.normalizePayload(payload, config)
		const timeline = normalized.canvasTimeline as ToolConfig
		const styles = {...timeline.styleClassByEventId as ToolConfig}
		if (classId) styles[eventId] = classId
		else delete styles[eventId]
		return {...normalized, canvasTimeline: {...timeline, styleClassByEventId: styles}}
	}

	setGroups(payload: EnginePayload = {}, groups: ToolConfig[] = [], config: ToolConfig = {}): EnginePayload {
		const normalized = this.normalizePayload(payload, config)
		const timeline = normalized.canvasTimeline as ToolConfig
		return {...normalized, canvasTimeline: {...timeline, groups}}
	}
}
